import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext } from "@dnd-kit/core";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";

import {
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../../Firestore/nutrition/nutritionPaths";
import { useToast } from "../../../contexts/ToastContext";
import { useScreenDimensions } from "../../../_layout";
import { normalizeClientNutritionPlanId } from "./clientNutritionBrowserUtils";
import {
  buildClientNutritionBrowserSections,
  getClientNutritionFolderTargetFromOver,
  isClientNutritionPlanMovePending,
  isClientNutritionCrossFolderMove,
} from "./clientNutritionBrowserUtils";
import ClientNutritionFolderSection from "./ClientNutritionFolderSection";
import useClientNutritionFolderDragKit from "./useClientNutritionFolderDragKit";

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  foldersContainer: {
    gap: 4,
  },
});

const DEFAULT_EXPANDED_FOLDERS = {
  [CLIENT_NUTRITION_STATUS_ACTIVE]: true,
  [CLIENT_NUTRITION_STATUS_ARCHIVED]: true,
};

export const ClientNutritionPlansBrowser = observer(function ClientNutritionPlansBrowser({
  onDeletePlan,
  onDuplicatePlan,
  onExportPlanPdf,
  plans,
  planConflictSummariesById = {},
  onCopyToAthlete,
  onCreatePlan,
  onExportActivePlansPdf,
  onExportToLibrary,
  onImportFromLibrary,
  onOpenPlan,
  onMovePlan,
}) {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { sensors } = useClientNutritionFolderDragKit();
  const [expandedFolders, setExpandedFolders] = useState(DEFAULT_EXPANDED_FOLDERS);
  const [pendingStatusesByPlanId, setPendingStatusesByPlanId] = useState({});
  const [activeDragPlanId, setActiveDragPlanId] = useState(null);
  const [activeDragSourceFolderKey, setActiveDragSourceFolderKey] = useState(null);
  const [activeDragOverFolderKey, setActiveDragOverFolderKey] = useState(null);
  const [pressSuppressedPlanId, setPressSuppressedPlanId] = useState(null);
  const pressSuppressTimeoutRef = useRef(null);
  const pendingStatusesByPlanIdRef = useRef({});
  const browserPlans = Array.isArray(plans) ? plans : [];

  useEffect(
    () => () => {
      if (pressSuppressTimeoutRef.current) {
        clearTimeout(pressSuppressTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setPendingStatusesByPlanId((previousMap) => {
      const nextMap = { ...previousMap };
      let hasChanges = false;

      Object.entries(previousMap).forEach(([planId, pendingStatus]) => {
        const matchingPlan = browserPlans.find(
          (plan) => normalizeClientNutritionPlanId(plan?.id) === planId,
        );
        const resolvedStatus = String(matchingPlan?.status ?? "").trim().toLowerCase();

        if (!matchingPlan || resolvedStatus === pendingStatus) {
          delete nextMap[planId];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        pendingStatusesByPlanIdRef.current = nextMap;
      }
      return hasChanges ? nextMap : previousMap;
    });
  }, [browserPlans]);

  useEffect(() => {
    pendingStatusesByPlanIdRef.current = pendingStatusesByPlanId;
  }, [pendingStatusesByPlanId]);

  const menuItems = useMemo(
    () => [
      {
        key: "exportToLibrary",
        label: t("exportToLibrary-string", "Export to library"),
        icon: { type: "ionicons", name: "book-outline" },
      },
      {
        key: "copyToClient",
        label: t("copyToAnotherClient-string", "Copy to another athlete"),
        icon: { type: "ionicons", name: "person-add-outline" },
      },
      {
        key: "duplicatePlan",
        label: t("duplicate-string", "Duplicate"),
        icon: { type: "ionicons", name: "duplicate-outline" },
      },
      {
        key: "exportPdf",
        label: t("exportPdf-string", "Export PDF"),
        icon: { type: "ionicons", name: "print-outline" },
      },
      {
        key: "delete",
        label: t("delete-string", "Delete"),
        icon: { type: "ionicons", name: "trash-outline" },
        isDanger: true,
      },
    ],
    [t],
  );

  const sectionLabels = useMemo(
    () => ({
      activeTitle: t("active-string", "Active"),
      activeDescription: t(
        "clientNutritionPlanFoldersActiveDescription-string",
        "Active plans will be visible to the athlete.",
      ),
      activeEmptyText: t(
        "clientNutritionPlanFoldersNoActive-string",
        "No active nutrition plans yet.",
      ),
      activeCreateTooltip: t(
        "clientNutritionPlanFoldersCreateTooltip-string",
        "Create nutrition plan",
      ),
      archivedTitle: t("archived-string", "Archived"),
      archivedDescription: t(
        "clientNutritionPlanFoldersArchivedDescription-string",
        "Archived plans will not be visible to the athlete.",
      ),
      archivedEmptyText: t(
        "clientNutritionPlanFoldersNoArchived-string",
        "No archived nutrition plans yet.",
      ),
      archivedCreateTooltip: t(
        "clientNutritionPlanFoldersCreateArchivedTooltip-string",
        "Create archived nutrition plan",
      ),
    }),
    [t],
  );

  const folderSections = useMemo(
    () =>
      buildClientNutritionBrowserSections(
        browserPlans,
        pendingStatusesByPlanId,
        sectionLabels,
        language,
      ),
    [browserPlans, pendingStatusesByPlanId, sectionLabels, language],
  );

  const schedulePressSuppressionClear = useCallback(() => {
    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
    }

    pressSuppressTimeoutRef.current = setTimeout(() => {
      setPressSuppressedPlanId(null);
      pressSuppressTimeoutRef.current = null;
    }, 220);
  }, []);

  const clearDragState = useCallback(() => {
    setActiveDragPlanId(null);
    setActiveDragSourceFolderKey(null);
    setActiveDragOverFolderKey(null);
  }, []);

  const handleToggleFolder = useCallback((folderKey) => {
    setExpandedFolders((previousMap) => ({
      ...previousMap,
      [folderKey]: previousMap[folderKey] === false,
    }));
  }, []);

  const handleMenuAction = useCallback(
    (plan, itemKey) => {
      if (itemKey === "exportPdf") {
        onExportPlanPdf?.(plan);
        return;
      }

      if (itemKey === "exportToLibrary") {
        onExportToLibrary?.(plan);
        return;
      }

      if (itemKey === "copyToClient") {
        onCopyToAthlete?.(plan);
        return;
      }

      if (itemKey === "duplicatePlan") {
        onDuplicatePlan?.(plan);
        return;
      }

      if (itemKey === "delete") {
        onDeletePlan?.(plan);
        return;
      }
    },
    [
      onCopyToAthlete,
      onDeletePlan,
      onDuplicatePlan,
      onExportPlanPdf,
      onExportToLibrary,
    ],
  );

  const handlePlanDragStart = useCallback((event) => {
    const activeData = event?.active?.data?.current;
    if (!activeData || activeData.type !== "nutritionPlanRow") {
      return;
    }

    if (
      isClientNutritionPlanMovePending(
        pendingStatusesByPlanIdRef.current,
        activeData.planId,
      )
    ) {
      return;
    }

    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
      pressSuppressTimeoutRef.current = null;
    }

    setActiveDragPlanId(activeData.planId ?? null);
    setActiveDragSourceFolderKey(activeData.sourceFolderKey ?? CLIENT_NUTRITION_STATUS_ACTIVE);
    setActiveDragOverFolderKey(null);
    setPressSuppressedPlanId(activeData.planId ?? null);
  }, []);

  const handlePlanDragOver = useCallback((event) => {
    const targetFolderKey = getClientNutritionFolderTargetFromOver(
      event?.over?.data?.current,
    );
    setActiveDragOverFolderKey(targetFolderKey);
  }, []);

  const handlePlanDragCancel = useCallback(() => {
    clearDragState();
    schedulePressSuppressionClear();
  }, [clearDragState, schedulePressSuppressionClear]);

  const handleMovePlan = useCallback(
    async (plan, targetFolderKey) => {
      const normalizedPlanId = normalizeClientNutritionPlanId(plan?.id);
      if (!normalizedPlanId || typeof onMovePlan !== "function") {
        return;
      }

      if (
        isClientNutritionPlanMovePending(
          pendingStatusesByPlanIdRef.current,
          normalizedPlanId,
        )
      ) {
        return;
      }

      setPendingStatusesByPlanId((previousMap) => {
        const nextMap = {
          ...previousMap,
          [normalizedPlanId]: targetFolderKey,
        };
        pendingStatusesByPlanIdRef.current = nextMap;
        return nextMap;
      });

      try {
        await onMovePlan(plan, targetFolderKey);
      } catch (error) {
        setPendingStatusesByPlanId((previousMap) => {
          const nextMap = { ...previousMap };
          delete nextMap[normalizedPlanId];
          pendingStatusesByPlanIdRef.current = nextMap;
          return nextMap;
        });
        showToast("error-string", {
          type: "error",
          title: "error-string",
          description: "nutritionPlanSaveErrorMessage-string",
        });
      }
    },
    [onMovePlan, showToast],
  );

  const handlePlanDragEnd = useCallback(
    async (event) => {
      const activeData = event?.active?.data?.current;
      const targetFolderKey = getClientNutritionFolderTargetFromOver(
        event?.over?.data?.current,
      );

      clearDragState();
      schedulePressSuppressionClear();

      if (!activeData || activeData.type !== "nutritionPlanRow" || targetFolderKey == null) {
        return;
      }

      const sourceFolderKey =
        activeData.sourceFolderKey ?? CLIENT_NUTRITION_STATUS_ACTIVE;

      if (!isClientNutritionCrossFolderMove(sourceFolderKey, targetFolderKey)) {
        return;
      }

      const normalizedPlanId = normalizeClientNutritionPlanId(activeData.planId);
      if (
        isClientNutritionPlanMovePending(
          pendingStatusesByPlanIdRef.current,
          normalizedPlanId,
        )
      ) {
        return;
      }
      const plan = browserPlans.find(
        (entry) => normalizeClientNutritionPlanId(entry?.id) === normalizedPlanId,
      );

      if (!plan) {
        return;
      }

      await handleMovePlan(plan, targetFolderKey);
    },
    [
      browserPlans,
      clearDragState,
      handleMovePlan,
      schedulePressSuppressionClear,
    ],
  );

  return (
    <View
      style={[
        styles.container,
        { marginTop: screenSize === "small" ? 8 : 16 },
      ]}
    >
      <DndContext
        sensors={sensors}
        onDragStart={handlePlanDragStart}
        onDragOver={handlePlanDragOver}
        onDragEnd={handlePlanDragEnd}
        onDragCancel={handlePlanDragCancel}
      >
        <View style={styles.foldersContainer}>
          {folderSections.map((section) => (
            <ClientNutritionFolderSection
              key={section.folderKey}
              section={section}
              pendingStatusesByPlanId={pendingStatusesByPlanId}
              planConflictSummariesById={planConflictSummariesById}
              isExpanded={expandedFolders[section.folderKey] !== false}
              onToggleExpand={handleToggleFolder}
              onCreatePlan={onCreatePlan}
              onExportActivePlansPdf={onExportActivePlansPdf}
              onImportFromLibrary={onImportFromLibrary}
              onPressPlan={onOpenPlan}
              onMovePlan={handleMovePlan}
              menuItems={menuItems}
              onMenuAction={handleMenuAction}
              activeDragPlanId={activeDragPlanId}
              activeDragSourceFolderKey={activeDragSourceFolderKey}
              activeDragOverFolderKey={activeDragOverFolderKey}
              pressSuppressedPlanId={pressSuppressedPlanId}
            />
          ))}
        </View>
      </DndContext>
    </View>
  );
});

export default ClientNutritionPlansBrowser;
